import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import ScoreRing from '../components/ScoreRing';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Clock, ShieldCheck, BookOpen, Pause, RotateCcw, ArrowRight, Sparkles,
  Compass, Brain, Building2, Heart, Flame, ListChecks, Star, CheckCircle2,
  Leaf, Target, Share2, Info, ChevronDown } from 'lucide-react';
import celebrate from '../utils/celebrate';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/assessment`;

const stagger = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

const MODULE_CFG = [
  { key: 'interests', icon: Heart, label: 'Interests & Enjoyment', desc: 'Discover what types of work naturally energize you.', color: '#C01A49', instrument: 'RIASEC Interest Profiler' },
  { key: 'traits', icon: Star, label: 'Personality & Strengths', desc: 'Identify your core personality traits and work style.', color: '#ECC451', instrument: 'Big Five Personality Model' },
  { key: 'environment', icon: Flame, label: 'Work Environment', desc: 'Where you\'ll thrive, team size, pace, structure.', color: '#782FE1', instrument: 'Environment Preferences' },
  { key: 'values', icon: Leaf, label: 'Values & Motivation', desc: 'What the work has to give you to stay fulfilled.', color: '#15BA89', instrument: 'Work Values Assessment' },
];

export default function AssessmentPage() {
  const { getAuthHeader, user } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedSection = searchParams.get('section');
  const requestedView = searchParams.get('view');
  const [q, setQ] = useState(null);
  const [res, setRes] = useState(null);
  const [answers, setAnswers] = useState({});
  const [sectionIdx, setSectionIdx] = useState(0);
  const [view, setView] = useState('hub');
  const [saving, setSaving] = useState(false);
  const [showModuleDetails, setShowModuleDetails] = useState(false);

  const load = useCallback(async () => {
    const [qq, rr] = await Promise.all([
      axios.get(`${API}/questions`, getAuthHeader()),
      axios.get(`${API}/results`, getAuthHeader()),
    ]);
    setQ(qq.data);
    setAnswers(qq.data.answers || {});
    setRes(rr.data.started ? rr.data : null);
    if (rr.data.started && rr.data.complete) setView('results');
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!q?.sections) return;
    if (requestedView === 'intro') { setView('hub'); return; }
    if (!requestedSection) return;
    const idx = q.sections.findIndex((s) => s.key === requestedSection);
    if (idx >= 0) { setSectionIdx(idx); setView('quiz'); window.scrollTo({ top: 0 }); }
  }, [q, requestedSection, requestedView]);

  const section = q?.sections?.[sectionIdx];
  const scale = useMemo(() => (section?.scale === 'enjoy' ? q?.scales?.enjoy : q?.scales?.likert) || [], [q, section]);
  const answeredInSection = section ? section.items.filter(i => answers[i.id]).length : 0;

  const save = async (patch, { silent } = {}) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/answers`, { answers: patch }, getAuthHeader());
      if (!silent) toast.success(`Saved. ${r.data.progress}% done`);
      return r.data;
    } finally { setSaving(false); }
  };

  const pick = async (id, value) => {
    const next = { ...answers, [id]: value };
    setAnswers(next);
    await save({ [id]: value }, { silent: true });
  };

  const showResults = async () => {
    const r = await axios.get(`${API}/results`, getAuthHeader());
    setRes(r.data.started ? r.data : null);
    setView('results');
    if (q?.progress === 100) celebrate('Assessment complete!');
  };

  const reset = async () => {
    await axios.post(`${API}/reset`, {}, getAuthHeader());
    toast.success('Reset complete.');
    setAnswers({}); setRes(null); setSectionIdx(0); setView('hub');
  };

  const startSection = (idx) => {
    setSectionIdx(idx);
    setView('quiz');
    window.scrollTo({ top: 0 });
  };

  if (!q) return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;

  const progress = q.progress || 0;
  const totalAnswered = Object.keys(answers).length;
  const estMinLeft = Math.max(1, Math.round((q.total_items - totalAnswered) * 0.3));

  // Map sections to module status
  const moduleStatus = MODULE_CFG.map((mod, i) => {
    const sec = q.sections[i];
    if (!sec) return { ...mod, status: 'not_started', pct: 0, answered: 0, total: 0 };
    const answered = sec.items.filter(item => answers[item.id]).length;
    const total = sec.items.length;
    const pct = total > 0 ? Math.round(answered / total * 100) : 0;
    const status = pct === 100 ? 'completed' : pct > 0 ? 'in_progress' : 'not_started';
    return { ...mod, status, pct, answered, total, minutes: sec.minutes };
  });

  const completedCount = moduleStatus.filter(m => m.status === 'completed').length;
  const inProgressCount = moduleStatus.filter(m => m.status === 'in_progress').length;
  const notStartedCount = moduleStatus.filter(m => m.status === 'not_started').length;

  /* ═══════ HUB VIEW, Assessment Dashboard ═══════ */
  if (view === 'hub') return (
    <AppLayout>
      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-5 max-w-[1400px] mx-auto" data-testid="assessment-hub">

        {/* HEADER */}
        <motion.div variants={fadeUp}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.5))' }} />
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#F4F7FF]">Career Fit Assessment</h1>
            </div>
            <div className="flex items-center gap-3">
              <button className="text-xs text-[#C6D4E8] hover:text-white transition-colors flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Learn how it works</button>
              <button className="text-xs text-[#C6D4E8] hover:text-white transition-colors flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> Share with counselor</button>
            </div>
          </div>
          <p className="text-sm text-[#C6D4E8] mt-1 max-w-2xl">Discover your interests, strengths, values, and work style, every career recommendation builds on what you tell us here.</p>
        </motion.div>

        {/* STUDENT INFO BAR */}
        <motion.div variants={fadeUp}>
          <div className="flex items-center gap-6 flex-wrap rounded-xl px-5 py-3.5" style={{
            background: 'rgba(6,23,54,0.72)', backdropFilter: 'blur(16px)',
            border: '1px solid rgba(57,227,220,0.22)',
          }} data-testid="student-info-bar">
            {[
              { label: user?.grade_level ? 'Grade' : 'Major', value: user?.grade_level ? `Grade ${user.grade_level}` : (user?.major || 'Undeclared'), icon: BookOpen },
              { label: 'Year', value: user?.academic_year ? user.academic_year.charAt(0).toUpperCase() + user.academic_year.slice(1) : (user?.grade_level ? `${user.grade_level}th Grade` : 'Student'), icon: Target },
              { label: 'Goal', value: user?.career_target || user?.career_aspirations || (user?.interests?.[0] ? user.interests[0].charAt(0).toUpperCase() + user.interests[0].slice(1) : 'Exploring'), icon: Compass },
              { label: 'Status', value: progress === 100 ? 'Complete' : progress > 0 ? 'In Progress' : 'Not Started', icon: ListChecks },
              { label: 'Est. time left', value: progress === 100 ? 'Done' : `${estMinLeft} min`, icon: Clock },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 3px rgba(57,227,220,0.4))' }} />
                  <span className="text-xs text-[#C6D4E8]">{item.label}</span>
                  <span className="text-xs text-white font-bold">{item.value}</span>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* MAIN GRID: Progress + Sidebar */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
          {/* LEFT */}
          <div className="xl:col-span-8 space-y-5">
            {/* Progress Card */}
            <motion.div variants={fadeUp}>
              <div className="rounded-[16px] p-6" style={{
                background: 'rgba(6,23,54,0.72)', backdropFilter: 'blur(16px)',
                border: '1px solid rgba(120,47,225,0.35)',
                boxShadow: '0 0 8px rgba(120,47,225,0.45), 0 0 20px rgba(120,47,225,0.20)',
              }}>
                <div className="flex items-center gap-6">
                  {/* Gauge */}
                  <div className="w-32 h-32 relative flex-shrink-0">
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(89,126,190,0.15)" strokeWidth="2.5" />
                      <motion.circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="2.5" strokeLinecap="round"
                        initial={{ strokeDasharray: '0 97.4' }} animate={{ strokeDasharray: `${progress * 0.974} 97.4` }}
                        transition={{ duration: 1.2, ease: 'easeOut' }} stroke="url(#assessGrad)"
                        style={{ filter: 'drop-shadow(0 0 6px rgba(120,47,225,0.6))' }} />
                      <defs><linearGradient id="assessGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#782FE1" /><stop offset="100%" stopColor="#39E3DC" /></linearGradient></defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-outfit font-black text-white">{progress}%</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-outfit font-bold text-lg mb-2">Your assessment progress</p>
                    <p className="text-[11px] text-[#C6D4E8] mb-3">Complete all modules to unlock personalized career matches, strategy recommendations, and proof targets.</p>
                    <div className="flex gap-4 text-xs">
                      <span className="text-[#15BA89] font-semibold">{completedCount} Completed</span>
                      <span className="text-[#ECC451] font-semibold">{inProgressCount} In progress</span>
                      <span className="text-[#C6D4E8]">{notStartedCount} Not started</span>
                    </div>
                    <div className="h-2 rounded-full bg-[rgba(89,126,190,0.12)] mt-3 overflow-hidden">
                      <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.8 }} style={{ background: 'linear-gradient(90deg, #782FE1, #39E3DC)', boxShadow: '0 0 8px rgba(57,227,220,0.4)' }} />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Why this matters */}
            <motion.div variants={fadeUp}>
              <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(89,126,190,0.22)' }}>
                <p className="text-white font-outfit font-bold text-base mb-3">Why this matters</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'Unlock better-fit career matches based on your real profile',
                    'See which majors and minors strengthen your path',
                    'Get a custom strategy built around your strengths',
                    'Spot skill gaps before employers do',
                    'Receive personalized proof-building recommendations',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-[#C6D4E8]">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#15BA89] mt-0.5 flex-shrink-0" /> {item}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="xl:col-span-4 space-y-4">
            <motion.div variants={fadeUp}>
              <div className="rounded-[16px] p-5" style={{
                background: 'linear-gradient(145deg, rgba(19,22,58,0.92), rgba(27,13,64,0.82))',
                border: '1px solid rgba(120,47,225,0.55)',
                boxShadow: '0 0 8px rgba(120,47,225,0.35), 0 0 16px rgba(120,47,225,0.15)',
              }}>
                <Sparkles className="w-5 h-5 text-[#ECC451] mb-2" style={{ filter: 'drop-shadow(0 0 4px rgba(236,196,81,0.5))' }} />
                <p className="text-white font-outfit font-bold text-sm">Next unlocked output</p>
                <p className="text-lg font-outfit font-black text-[#39E3DC] mt-1">Top Career Matches</p>
                <p className="text-[11px] text-[#C6D4E8] mt-1">Complete {Math.max(0, 3 - completedCount)} more modules to unlock.</p>
                <button onClick={() => { setView('quiz'); setSectionIdx(moduleStatus.findIndex(m => m.status !== 'completed') || 0); }}
                  className="fp-btn-primary w-full py-2 rounded-xl text-xs mt-3">
                  {progress > 0 ? 'Continue Assessment' : 'Start Assessment'}
                </button>
              </div>
            </motion.div>

            <motion.div variants={fadeUp}>
              <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(89,126,190,0.22)' }}>
                <p className="text-white font-outfit font-bold text-sm mb-1">Counselor-ready summary</p>
                <p className="text-[11px] text-[#C6D4E8]">Once complete, share a professional summary with your academic counselor.</p>
                {progress > 0 && (
                  <button onClick={showResults} className="text-xs text-[#39E3DC] mt-2 hover:underline">Preview summary →</button>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* ASSESSMENT MODULES */}
        <motion.div variants={fadeUp}>
          <p className="text-white font-outfit font-bold text-lg mb-3">Assessment Modules</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {moduleStatus.map((mod, i) => {
              const Icon = mod.icon;
              const statusBadge = mod.status === 'completed'
                ? { label: 'Completed', bg: 'rgba(21,186,137,0.12)', border: 'rgba(21,186,137,0.3)', color: '#15BA89' }
                : mod.status === 'in_progress'
                ? { label: 'In Progress', bg: 'rgba(236,196,81,0.12)', border: 'rgba(236,196,81,0.3)', color: '#ECC451' }
                : { label: 'Not Started', bg: 'rgba(168,180,208,0.08)', border: 'rgba(168,180,208,0.2)', color: '#C6D4E8' };
              return (
                <div key={mod.key} className="rounded-xl p-4 flex flex-col" style={{
                  background: 'rgba(6,23,54,0.72)', backdropFilter: 'blur(12px)',
                  border: `1px solid ${mod.status === 'completed' ? 'rgba(21,186,137,0.3)' : mod.status === 'in_progress' ? 'rgba(236,196,81,0.3)' : 'rgba(89,126,190,0.22)'}`,
                  boxShadow: mod.status === 'completed'
                    ? '0 0 8px rgba(21,186,137,0.35), 0 0 20px rgba(21,186,137,0.12)'
                    : mod.status === 'in_progress'
                    ? '0 0 8px rgba(236,196,81,0.35), 0 0 20px rgba(236,196,81,0.12)'
                    : '0 0 6px rgba(57,227,220,0.12), inset 0 0 12px rgba(57,227,220,0.02)',
                }} data-testid={`module-${mod.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{
                      backgroundColor: statusBadge.bg, border: `1px solid ${statusBadge.border}`, color: statusBadge.color,
                    }}>{statusBadge.label}</span>
                    <span className="text-[10px] text-[#C6D4E8] font-semibold">{mod.pct}%</span>
                  </div>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2"
                    style={{ background: `${mod.color}15`, border: `1px solid ${mod.color}30` }}>
                    <Icon className="w-4 h-4" style={{ color: mod.color }} />
                  </div>
                  <p className="text-xs text-white font-semibold leading-tight">{i + 1}. {mod.label}</p>
                  <p className="text-[10px] text-[#C6D4E8] mt-1 leading-relaxed">{mod.desc}</p>
                  {mod.minutes && <p className="text-[10px] text-slate-300 mt-1">{mod.minutes} min</p>}
                  {/* Progress bar */}
                  <div className="h-1.5 rounded-full bg-[rgba(89,126,190,0.12)] mt-2 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${mod.pct}%`, background: mod.color, boxShadow: `0 0 6px ${mod.color}55` }} />
                  </div>
                  <div className="pt-2">
                  <button onClick={() => startSection(i)} className="mt-auto w-full py-2 rounded-lg text-[11px] font-semibold transition-all hover:scale-[1.02]" style={{
                    background: mod.status === 'in_progress' ? 'rgba(21,186,137,0.12)' : mod.status === 'completed' ? 'rgba(89,126,190,0.08)' : 'rgba(57,227,220,0.08)',
                    border: `1px solid ${mod.status === 'in_progress' ? 'rgba(21,186,137,0.3)' : mod.status === 'completed' ? 'rgba(89,126,190,0.2)' : 'rgba(57,227,220,0.25)'}`,
                    color: mod.status === 'in_progress' ? '#15BA89' : mod.status === 'completed' ? '#C6D4E8' : '#39E3DC',
                  }} data-testid={`module-cta-${mod.key}`}>
                    {mod.status === 'completed' ? 'Review results' : mod.status === 'in_progress' ? 'Continue' : 'Start'}
                  </button>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* BOTTOM ROW */}
        <motion.div variants={fadeUp}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Question Preview */}
            <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(89,126,190,0.22)' }}>
              <p className="text-white font-outfit font-bold text-sm mb-3">Question preview</p>
              <div className="rounded-xl border border-[rgba(89,126,190,0.15)] bg-[#0B1F46] p-4 mb-3">
                <p className="text-sm text-white italic">"I enjoy solving complex problems that require creative thinking and multiple perspectives."</p>
              </div>
              <div className="flex gap-2">
                {['Strongly dislike', 'Dislike', 'Neutral', 'Enjoy', 'Strongly enjoy'].map((opt, i) => (
                  <button key={i} className="flex-1 py-2 rounded-lg text-[9px] text-[#C6D4E8] border border-[rgba(89,126,190,0.18)] bg-[#0B1F46] hover:border-[rgba(57,227,220,0.3)] transition-all">
                    {opt}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-300 mt-2 text-center">Sample question · Your actual questions will vary</p>
            </div>

            {/* Assessment Science */}
            <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(89,126,190,0.22)' }}>
              <p className="text-white font-outfit font-bold text-sm mb-3">Assessment science made simple</p>
              <p className="text-[11px] text-[#C6D4E8] leading-relaxed mb-3">FuturePath uses established instruments, the same ones used by career centers and research institutions. No AI scores your answers. Same answers always produce the same result.</p>
              <div className="space-y-1.5">
                {['Based on O*NET Interest Profiler & Big Five model', 'Used by 1,000+ universities and career centers', 'No AI guessing, deterministic scoring only', 'Results feed every recommendation in FuturePath'].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] text-[#C6D4E8]">
                    <CheckCircle2 className="w-3 h-3 text-[#15BA89] mt-0.5 flex-shrink-0" /> {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* FOOTER */}
        <motion.div variants={fadeUp}>
          <div className="flex items-center justify-center gap-6 py-3 text-[10px] text-slate-300">
            <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Your data is private and secure</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Saving your progress automatically</span>
          </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );

  /* ═══════ QUIZ VIEW ═══════ */
  if (view === 'quiz' && section) return (
    <AppLayout>
      <div className="max-w-[1000px] mx-auto space-y-5" data-testid="assessment-quiz">
        {/* Quiz header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#39E3DC]">
              Section {sectionIdx + 1} of {q.sections.length} · {section.instrument}
            </p>
            <h2 className="text-xl font-outfit font-bold text-white">{section.label}</h2>
          </div>
          <button onClick={() => setView('hub')} className="text-xs text-[#C6D4E8] hover:text-white">← Back to overview</button>
        </div>

        {/* Progress */}
        <div className="rounded-xl border border-[rgba(89,126,190,0.18)] bg-[#061736] px-4 py-3">
          <div className="flex items-center justify-between text-xs text-[#C6D4E8] mb-1.5">
            <span>{answeredInSection}/{section.items.length} answered in this section</span>
            <span>{section.minutes} min estimated</span>
          </div>
          <div className="h-2 rounded-full bg-[rgba(89,126,190,0.12)] overflow-hidden">
            <div className="h-full rounded-full" style={{
              width: `${section.items.length > 0 ? (answeredInSection / section.items.length * 100) : 0}%`,
              background: 'linear-gradient(90deg, #782FE1, #39E3DC)', boxShadow: '0 0 6px rgba(57,227,220,0.3)',
            }} />
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-3">
          {section.items.map((item, i) => (
            <div key={item.id} className="rounded-xl p-4" style={{
              background: answers[item.id] ? 'rgba(21,186,137,0.04)' : 'rgba(6,23,54,0.72)',
              border: `1px solid ${answers[item.id] ? 'rgba(21,186,137,0.2)' : 'rgba(89,126,190,0.18)'}`,
            }} data-testid={`item-${item.id}`}>
              <p className="text-sm text-white mb-3">
                <span className="text-[#C6D4E8] mr-2">{i + 1}.</span>
                {section.scale === 'enjoy' ? <span className="text-[#C6D4E8]">How would you feel about: </span> : null}
                {item.text}
              </p>
              <div className="flex flex-wrap gap-2">
                {scale.map(opt => {
                  const on = answers[item.id] === opt.value;
                  return (
                    <button key={opt.value} onClick={() => pick(item.id, opt.value)}
                      data-testid={`answer-${item.id}-${opt.value}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={on
                        ? { borderColor: '#39E3DC', background: 'rgba(57,227,220,0.15)', color: '#fff', border: '1px solid #39E3DC', boxShadow: '0 0 8px rgba(57,227,220,0.3)' }
                        : { border: '1px solid rgba(89,126,190,0.22)', color: '#C6D4E8', background: 'rgba(11,31,70,0.4)' }
                      }>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex flex-wrap items-center gap-3 pt-2">
          {sectionIdx > 0 && (
            <button onClick={() => setSectionIdx(sectionIdx - 1)} className="text-xs text-[#C6D4E8] hover:text-white">← Previous section</button>
          )}
          {sectionIdx < q.sections.length - 1 ? (
            <button onClick={() => setSectionIdx(sectionIdx + 1)} className="fp-btn-primary px-5 py-2 rounded-xl text-sm" data-testid="next-section-btn">
              Next section <ArrowRight className="w-4 h-4 ml-1 inline" />
            </button>
          ) : (
            <button onClick={showResults} className="fp-btn-primary px-5 py-2 rounded-xl text-sm" data-testid="finish-assessment-btn">See my results</button>
          )}
          <button onClick={showResults} className="text-xs text-[#C6D4E8] hover:text-[#39E3DC]" data-testid="stop-here-btn">
            Stop here and score what I've done
          </button>
          <span className="text-[10px] text-slate-300 ml-auto">
            {saving ? 'Saving...' : 'Every answer saves automatically'}
          </span>
        </div>
      </div>
    </AppLayout>
  );

  /* ═══════ RESULTS VIEW ═══════ */
  if (view === 'results' && res) return (
    <AppLayout>
      <div className="max-w-[1200px] mx-auto space-y-5" data-testid="assessment-results">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white">Your Assessment Results</h1>
            <p className="text-sm text-[#C6D4E8]">{res.complete ? 'Complete profile' : `${res.answered} of ${res.of} answered, partial results`}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setView('hub')} className="text-xs text-[#C6D4E8] hover:text-white">← Overview</button>
            <button onClick={() => { setView('quiz'); setSectionIdx(0); }} className="fp-btn-primary px-4 py-2 rounded-xl text-xs">
              {res.complete ? 'Review answers' : 'Continue assessment'}
            </button>
            <button onClick={reset} className="text-xs text-slate-300 hover:text-white flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reset</button>
          </div>
        </div>

        {!res.complete && (
          <div className="p-3 rounded-xl border border-[rgba(236,196,81,0.3)] bg-[rgba(236,196,81,0.06)] text-sm text-[#ECC451]">
            You've answered {res.answered} of {res.of}. Finish the rest any time, it re-scores instantly.
          </div>
        )}

        {/* Headline Type */}
        {res.headline_type && (
          <div className="rounded-[16px] p-5 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(6,23,54,0.85), rgba(20,14,52,0.75))', border: '1px solid rgba(120,47,225,0.35)', boxShadow: '0 0 24px rgba(120,47,225,0.15)' }}>
            <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, #782FE1, #39E3DC, transparent)' }} />
            <p className="text-[10px] uppercase tracking-wider text-[#782FE1] mb-1">Your FuturePath Type</p>
            <h2 className="text-2xl font-outfit font-extrabold text-white mb-2" style={{ textShadow: '0 0 20px rgba(120,47,225,0.3)' }}>{res.headline_type.name}</h2>
            <p className="text-xs text-[#C6D4E8]">{res.headline_type.derived_from}</p>
          </div>
        )}

        {/* Holland / Interests */}
        {res.holland && (res.holland.scores?.length > 0 || res.holland.traits?.length > 0) && (
          <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(57,227,220,0.22)', boxShadow: '0 0 1px rgba(57,227,220,0.3)' }}>
            <h3 className="text-white font-outfit font-bold text-base mb-1">Interest Profile (Holland Code)</h3>
            {res.holland.means && <p className="text-[10px] text-[#C6D4E8] mb-2">{res.holland.means}</p>}
            {res.holland.confidence && <p className="text-[10px] uppercase tracking-wider text-[#39E3DC] mb-3 font-semibold">Confidence: {res.holland.confidence.level} · {res.holland.confidence.answered}/{res.holland.confidence.of} answered</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {(res.holland.scores || res.holland.traits || []).map(t => {
                const HOLLAND_COLORS = { R: '#C01A49', I: '#39E3DC', A: '#DC5FB9', S: '#ECC451', E: '#782FE1', C: '#15BA89' };
                const c = HOLLAND_COLORS[t.key || t.code] || '#39E3DC';
                return (
                  <div key={t.key || t.code} className="rounded-xl p-3 text-center" style={{ background: `${c}08`, border: `1px solid ${c}30` }}>
                    <p className="text-xl font-outfit font-extrabold" style={{ color: c, textShadow: `0 0 12px ${c}40` }}>{t.score}%</p>
                    <p className="text-xs text-white font-semibold mt-0.5">{t.label || t.name}</p>
                    <p className="text-[9px] text-[#C6D4E8] mt-0.5">{t.blurb || t.code}</p>
                  </div>
                );
              })}
            </div>
            {(res.holland.code || res.holland.top_code) && (
              <p className="text-sm text-white mt-4">Your Holland Code: <span className="text-[#39E3DC] font-extrabold text-base" style={{ textShadow: '0 0 10px rgba(57,227,220,0.4)' }}>{res.holland.code || res.holland.top_code}</span>
                <span className="text-[10px] text-[#C6D4E8] ml-2">({(res.holland.scores || res.holland.traits || []).slice(0, 3).map(s => s.label || s.name).join(' → ')})</span>
              </p>
            )}
          </div>
        )}

        {/* Personality Traits (Big Five) */}
        {res.traits && (res.traits.rows?.length > 0 || res.traits.traits?.length > 0) && (
          <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(120,47,225,0.22)', boxShadow: '0 0 1px rgba(120,47,225,0.3)' }}>
            <h3 className="text-white font-outfit font-bold text-base mb-1">Personality Traits</h3>
            <p className="text-[10px] text-[#C6D4E8] mb-2">Big Five personality dimensions, how you naturally operate</p>
            {res.traits.confidence && <p className="text-[10px] uppercase tracking-wider text-[#782FE1] mb-3 font-semibold">Confidence: {res.traits.confidence.level} · {res.traits.confidence.answered}/{res.traits.confidence.of} answered</p>}
            <div className="space-y-4">
              {(res.traits.rows || res.traits.traits || []).map(t => {
                const TRAIT_COLORS = { O: '#DC5FB9', C: '#39E3DC', E: '#ECC451', A: '#15BA89', N: '#C01A49' };
                const c = TRAIT_COLORS[t.key] || t.color || '#782FE1';
                return (
                  <div key={t.key || t.label || t.name}>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm text-white font-bold w-36">{t.label || t.name}</span>
                      <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: 'rgba(89,126,190,0.08)' }}>
                        <div className="h-full rounded-full" style={{ width: `${t.score}%`, background: c, boxShadow: `0 0 8px ${c}50` }} />
                      </div>
                      <span className="text-sm font-extrabold w-12 text-right" style={{ color: c }}>{t.score}%</span>
                    </div>
                    {(t.so_what || t.meaning) && (
                      <p className="text-[10px] text-[#C6D4E8] ml-0 sm:ml-[9.5rem] leading-relaxed">{t.so_what || `This measures ${t.meaning}.`}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Work Environment Preferences */}
        {res.environment && (res.environment.rows?.length > 0 || res.environment.traits?.length > 0) && (
          <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(89,126,190,0.22)' }}>
            <h3 className="text-white font-outfit font-bold text-base mb-1">Work Environment Preferences</h3>
            <p className="text-[10px] text-[#C6D4E8] mb-3">The kind of workplace where you'll thrive</p>
            {res.environment.confidence && <p className="text-[10px] uppercase tracking-wider text-[#2E86E2] mb-3 font-semibold">Confidence: {res.environment.confidence.level} · {res.environment.confidence.answered}/{res.environment.confidence.of} answered</p>}
            <div className="space-y-4">
              {(res.environment.rows || res.environment.traits || []).map(t => {
                const score = t.score;
                const isNeutral = score >= 45 && score <= 55;
                const leansHigh = score > 55;
                const strength = t.strength ?? Math.abs(score - 50);
                const activeLabel = leansHigh ? (t.high_label || t.reads_as) : isNeutral ? 'No strong preference' : (t.low_label || t.reads_as);
                const activeColor = isNeutral ? '#C4D2E3' : leansHigh ? '#39E3DC' : '#2E86E2';
                return (
                  <div key={t.key || t.name}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] text-white capitalize font-semibold">{t.key || t.name}</span>
                      <span className="text-xs font-bold" style={{ color: activeColor }}>{activeLabel}</span>
                    </div>
                    {/* Bipolar bar: left label ← bar → right label */}
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] text-[#C6D4E8] w-20 text-right leading-tight hidden sm:block">{t.low_label || ''}</span>
                      <div className="flex-1 h-2 rounded-full relative" style={{ background: 'rgba(89,126,190,0.08)' }}>
                        {/* center line */}
                        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[rgba(89,126,190,0.25)]" />
                        {/* fill from center */}
                        {!isNeutral && (
                          <div className="absolute top-0 h-full rounded-full" style={{
                            left: leansHigh ? '50%' : `${score}%`,
                            width: `${strength}%`,
                            background: `linear-gradient(90deg, ${leansHigh ? '#39E3DC' : '#2E86E2'}, ${leansHigh ? '#15BA89' : '#39E3DC'})`,
                            boxShadow: `0 0 8px ${activeColor}40`,
                          }} />
                        )}
                        {isNeutral && (
                          <div className="absolute top-0 h-full rounded-full" style={{
                            left: '46%', width: '8%',
                            background: 'rgba(74,96,128,0.4)',
                          }} />
                        )}
                      </div>
                      <span className="text-[9px] text-[#C6D4E8] w-20 leading-tight hidden sm:block">{t.high_label || ''}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Work Values */}
        {res.values && (res.values.rows?.length > 0 || res.values.traits?.length > 0) && (
          <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(21,186,137,0.22)' }}>
            <h3 className="text-white font-outfit font-bold text-base mb-1">Work Values</h3>
            <p className="text-[10px] text-[#C6D4E8] mb-3">What matters most to you in a career, ranked</p>
            {res.values.confidence && <p className="text-[10px] uppercase tracking-wider text-[#15BA89] mb-3 font-semibold">Confidence: {res.values.confidence.level} · {res.values.confidence.answered}/{res.values.confidence.of} answered</p>}
            <div className="space-y-2.5">
              {(res.values.rows || res.values.traits || []).map((t, i) => (
                <div key={t.key || t.name} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-[#15BA89] w-5">{i + 1}.</span>
                  <span className="text-xs text-white font-semibold w-36">{t.label || t.name}</span>
                  <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(89,126,190,0.08)' }}>
                    <div className="h-full rounded-full" style={{ width: `${t.score}%`, background: 'linear-gradient(90deg, #15BA89, #39E3DC)', boxShadow: '0 0 6px rgba(21,186,137,0.4)' }} />
                  </div>
                  <span className="text-xs font-bold text-[#15BA89] w-10 text-right">{t.score}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strengths, backend sends plain string array */}
        {res.strengths?.length > 0 && (
          <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(57,227,220,0.22)' }}>
            <h3 className="text-white font-outfit font-bold text-base mb-3">Your Key Strengths</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(Array.isArray(res.strengths) ? res.strengths : res.strengths.traits || []).map((t, i) => (
                <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(21,186,137,0.04)', border: '1px solid rgba(21,186,137,0.22)' }}>
                  <p className="text-sm text-white font-semibold leading-snug">{typeof t === 'string' ? t : (t.name || t)}</p>
                  {t.description && <p className="text-[10px] text-[#C6D4E8] mt-1">{t.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Derailers */}
        {res.derailers?.length > 0 && (
          <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(192,26,73,0.22)' }}>
            <h3 className="text-white font-outfit font-bold text-base mb-3">Watch Out For</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {res.derailers.map((d, i) => (
                <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(46,134,226,0.04)', border: '1px solid rgba(46,134,226,0.22)' }}>
                  <p className="text-sm text-white font-semibold leading-snug">{typeof d === 'string' ? d : (d.name || d)}</p>
                  {d.description && <p className="text-[10px] text-[#C6D4E8] mt-1">{d.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Career Matches */}
        {res.career_matches?.length > 0 && (
          <div className="rounded-[16px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(57,227,220,0.28)', boxShadow: '0 0 8px rgba(57,227,220,0.3)' }}>
            <h3 className="text-white font-outfit font-bold text-base mb-3">Career Matches Based on Your Profile</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {res.career_matches.map((m, i) => (
                <div key={i} className="rounded-xl border border-[rgba(57,227,220,0.18)] bg-[#0B1F46] p-3">
                  <p className="text-sm text-white font-semibold">{m.title || m.name || m}</p>
                  {m.fit && <p className="text-[10px] text-[#39E3DC] font-bold mt-0.5">Fit: {m.fit}%</p>}
                  {m.why && <p className="text-[10px] text-[#C6D4E8] mt-1">{m.why}</p>}
                </div>
              ))}
            </div>
            <Link to="/app/career-intelligence" className="mt-4 flex items-center gap-1 text-xs text-[#39E3DC] hover:underline">
              Explore all career paths <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </div>
    </AppLayout>
  );

  /* Fallback */
  return <AppLayout><div className="text-center py-20 text-[#C6D4E8]">Loading assessment...</div></AppLayout>;
}
